/**
 * API Client Factory
 * Creates and configures API service instances
 */

import type { AxiosInstance } from "axios";
import { useMemo } from "react";
import { createAuthenticatedAxios } from "../services/axios-config";
import type { GffpBankFieldsService } from "./contracts/gffp-bank-fields.contract";
import type { KycService } from "./contracts/kyc.contract";
import type { LocationsService } from "./contracts/locations.contract";
import type { ProfileService } from "./contracts/profile.contract";
import type { QuoteService } from "./contracts/quote.contract";
import type { ReferenceDataService } from "./contracts/reference-data.contract";
import type { SendCommitService } from "./contracts/send-commit.contract";
import type { ServiceOptionsService } from "./contracts/service-options.contract";
import { MoneyGramGffpBankFieldsService } from "./services/gffp-bank-fields.service";
import { KycService as MoneyGramKycService } from "./services/kyc.service";
import { MoneyGramLocationsService } from "./services/locations.service";
import { MoneyGramProfileService } from "./services/profile.service";
import { MoneyGramQuoteService } from "./services/quote.service";
import { MoneyGramReferenceDataService } from "./services/reference-data.service";
import { MoneyGramSendCommitService } from "./services/send-commit.service";
import { MoneyGramServiceOptionsService } from "./services/service-options.service";
import { TransactionService } from "./services/transaction.service";

export interface ApiClientConfig {
	authToken: string;
	baseURL?: string;
	timeout?: number;
	apiVersion?: string;
	walletAddress?: string;
	transactionId?: string;
}

export interface ApiClient {
	referenceData: ReferenceDataService;
	serviceOptions: ServiceOptionsService;
	quote: QuoteService;
	sendCommit: SendCommitService;
	transaction: TransactionService;
	locations: LocationsService;
	gffpBankFields: GffpBankFieldsService;
	profile: ProfileService;
	kyc: KycService;
}

/**
 * Creates a configured API client with all services
 */
export function createApiClient(config: ApiClientConfig): ApiClient {
	const httpClient: AxiosInstance = createAuthenticatedAxios({
		authToken: config.authToken,
		baseURL: config.baseURL,
		timeout: config.timeout,
		apiVersion: config.apiVersion,
		walletAddress: config.walletAddress,
		transactionId: config.transactionId,
	});

	return {
		referenceData: new MoneyGramReferenceDataService(httpClient),
		serviceOptions: new MoneyGramServiceOptionsService(httpClient),
		quote: new MoneyGramQuoteService(httpClient),
		sendCommit: new MoneyGramSendCommitService(httpClient),
		transaction: new TransactionService(httpClient),
		locations: new MoneyGramLocationsService(httpClient),
		gffpBankFields: new MoneyGramGffpBankFieldsService(httpClient),
		profile: new MoneyGramProfileService(httpClient),
		kyc: new MoneyGramKycService(httpClient),
	};
}

/**
 * React Hook for API client with better memoization
 * This can be used in components to get a configured API client
 */
export function useApiClient(config: ApiClientConfig): ApiClient {
	// Memoize the API client based on individual config properties
	// This prevents unnecessary recreation when config object reference changes
	// but the actual values remain the same
	return useMemo(() => {
		return createApiClient(config);
	}, [
		config.authToken,
		config.baseURL,
		config.timeout,
		config.apiVersion,
		config.walletAddress,
		config.transactionId,
		config,
	]);
}
